import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { Clock, ChevronDownIcon, Info } from "lucide-react";
import { RRule } from "rrule";
import { addDays, format, isValid, parse } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Label } from "../../@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Textarea } from "../../@/components/ui/textarea";
import { Checkbox } from "../../@/components/ui/checkbox";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "../../@/components/ui/popover";
import axios from "axios";
import { toast } from "sonner";
import { api_endpoint } from "../constants/constant";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Calendar } from "../../@/components/ui/calendar";
import { TimePicker } from "../../@/components/ui/time-picker";
import { ClipLoader } from "react-spinners";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField } from "@/components/ui/form";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "../../@/components/ui/field";
import { cn } from "@/lib/utils";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import StudentSelectorDialog from "./studentSelector";
import AttendeesDialog from "./attendeesDialog";
import AttendeesBulkDialog from "./attendeesBulkDialog";
import { fromZonedTime } from "date-fns-tz";
import { getUserTimezone } from "../lib/timezone";

/**
 * Convert local time (HH:mm) to UTC ISO string based on user's timezone
 * @param {string} dateYYYYmmdd - Date in YYYY-MM-DD format
 * @param {string} timeHHmm - Time in HH:mm format
 * @param {string|null} timezone - Optional timezone override
 * @returns {string} UTC ISO string or empty string on error
 */
function toUtcISOStringFromLocalDateAndTime(
  dateYYYYmmdd,
  timeHHmm,
  timezone = null,
) {
  const datePart = String(dateYYYYmmdd || "").trim();
  const timePart = String(timeHHmm || "").trim();
  if (!datePart || !timePart) return "";

  // Normalize time to HH:mm format
  const timeMatch = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(timePart);
  if (!timeMatch) return "";
  const hhmm = `${timeMatch[1].padStart(2, "0")}:${timeMatch[2]}`;

  // Use provided timezone, or get user's timezone (localStorage > browser)
  const userTimezone = timezone || getUserTimezone();

  // Interpret chosen date+time as user's wall-clock, then serialize as UTC ISO.
  const [y, m, d] = datePart.split("-").map((n) => Number(n));
  if (![y, m, d].every((n) => Number.isFinite(n))) return "";
  const [hh, mm] = hhmm.split(":").map((n) => Number(n));
  if (![hh, mm].every((n) => Number.isFinite(n))) return "";

  const wallClock = new Date(y, m - 1, d, hh, mm, 0, 0);
  if (Number.isNaN(wallClock.getTime())) return "";

  const utcDate = fromZonedTime(wallClock, userTimezone);
  return utcDate.toISOString();
}

const recurrenceSchema = z.object({
  count: z.coerce.number().optional(),
  daysOfWeek: z.array(z.string()).optional(),
  endDate: z.string().optional(),
  endType: z.string().optional(),
  interval: z.coerce.number().optional(),
  type: z.string().optional(),
  unit: z.string().optional(),
  monthOption: z.string().optional(),
  monthWeek: z.string().optional(),
  monthWeekday: z.string().optional(),
});

const classSchema = z
  .object({
    title: z.string().min(1, { message: "Title is required" }),
    cohortId: z.coerce.number().optional(),
    courseId: z.coerce.number().optional(),
    tmId: z.string().optional(),
    ownerId: z.string().min(1, { message: "Owner is required" }),
    room: z.string().min(1, { message: "Room is required" }),
    startTime: z.string().min(1, { message: "Start time is required" }),
    date: z.string().min(1, { message: "Date is required" }),
    endTime: z.string().min(1, { message: "End time is required" }),
    attendees: z.array(z.string()),
    autoPopulate: z.coerce.number().optional(),
    classMode: z.string().min(1, { message: "Class mode is required" }),
    classType: z.string().min(1, { message: "Class type is required" }),
    deliveredVia: z.string().min(1, { message: "Delivery method is required" }),
    description: z.string(),
    location: z.string().min(1, { message: "Location is required" }),
    meetingLink: z.string().optional(),
    recurrence: recurrenceSchema.optional(),
  })
  .superRefine((data, ctx) => {
    // Only validate attendees if autoPopulate is not set
    if (
      (!data.autoPopulate ||
        data.autoPopulate === 0 ||
        data.autoPopulate === "") &&
      (!data.attendees || data.attendees.length === 0)
    ) {
      ctx.addIssue({
        path: ["attendees"],
        code: z.ZodIssueCode.custom,
        message: "Select at least one attendee",
      });
    }

    if (
      String(data.classMode || "").toLowerCase() === "online" &&
      String(data.deliveredVia || "").toLowerCase() === "zoom"
    ) {
      const meetingLink = String(data.meetingLink || "").trim();
      if (!meetingLink) {
        ctx.addIssue({
          path: ["meetingLink"],
          code: z.ZodIssueCode.custom,
          message: "Meeting link is required",
        });
      }
    }

    const start = String(data.startTime || "").trim();
    const end = String(data.endTime || "").trim();
    if (start && end && start.includes(":") && end.includes(":")) {
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      if ([sh, sm, eh, em].every((n) => Number.isFinite(n))) {
        const startMinutes = sh * 60 + sm;
        const endMinutes = eh * 60 + em;
        if (endMinutes <= startMinutes) {
          ctx.addIssue({
            path: ["endTime"],
            code: z.ZodIssueCode.custom,
            message: "End time must be after start time",
          });
        }
      }
    }
  });

export default function AddClassForm({ fetchClasses }) {
  const [open, setOpen] = useState(false);
  const [deliveredViaHover, setDeliveredViaHover] = useState(null);
  const [showRecurrence, setShowRecurrence] = useState(false);
  const [recurrenceSelectOpen, setRecurrenceSelectOpen] = useState(false);
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [showAttendeesDialog, setShowAttendeesDialog] = useState(false);
  const [showBulkAttendeesDialog, setShowBulkAttendeesDialog] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [studentsInCohort, setStudentsInCohort] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [allTms, setAllTms] = useState([]);
  const [openDatePopover, setOpenDatePopover] = useState(false);
  const [openRecurrenceDatePopover, setOpenRecurrenceDatePopover] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCohorts, setSelectedCohorts] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedTm, setSelectedTm] = useState("");
  const prevTmIdRef = useRef("");
  const [populateBy, setPopulateBy] = useState("cohort");
  const [dialogOccurrences, setDialogOccurrences] = useState([]);
  const { getToken } = useAuth();
  const { user } = useUser();

  const inputClasses =
    "w-full font-body text-sm bg-(--neutral-50) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) focus-visible:ring-1 focus-visible:ring-[var(--neutral-300)] focus-visible:border-(--neutral-300)";
  const selectTriggerClasses =
    "w-full h-11 border border-(--neutral-300) bg-(--surface-base) text-left text-(--neutral-900) focus:ring-1 focus:ring-[var(--color-accent)]";
  const outlineButtonClasses = "";

  const form = useForm({
    resolver: zodResolver(classSchema),
    defaultValues: {
      title: "",
      cohortId: 0,
      courseId: 0,
      tmId: "",
      ownerId: user?.id || "",
      room: "",
      startTime: "",
      date: "",
      endTime: "",
      attendees: [],
      autoPopulate: 0,
      classMode: "",
      classType: "",
      deliveredVia: "",
      description: "",
      location: "",
      meetingLink: "",
      recurrence: {
        type: "none",
        interval: 1,
        daysOfWeek: [],
        endType: "count",
        count: 1,
        endDate: "",
        unit: "Week",
        monthOption: "dayOfMonth",
        monthWeekday: "Mon",
        monthWeek: "first",
      },
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Ensure ownerId is set once Clerk user is available.
  useEffect(() => {
    const currentOwnerId = String(form.getValues("ownerId") || "").trim();
    const clerkUserId = String(user?.id || "").trim();
    if (!currentOwnerId && clerkUserId) {
      form.setValue("ownerId", clerkUserId, { shouldValidate: true });
    }
  }, [user?.id]);

  const syncSelectedTm = (value) => {
    const normalizedValue = value || "";
    setSelectedTm(normalizedValue);
    form.setValue("tmId", normalizedValue);
  };

  const watchedRecurrence = form.watch("recurrence");

  const recurrenceForm = useForm({
    defaultValues: {
      type: watchedRecurrence?.type || "none",
      interval: watchedRecurrence?.interval ?? 1,
      daysOfWeek: watchedRecurrence?.daysOfWeek ?? [],
      endType: watchedRecurrence?.endType || "count",
      count: watchedRecurrence?.count ?? 1,
      endDate: watchedRecurrence?.endDate || "",
      unit: watchedRecurrence?.unit || "Week",
      monthOption: watchedRecurrence?.monthOption || "dayOfMonth",
      monthWeekday: watchedRecurrence?.monthWeekday || "Mon",
      monthWeek: watchedRecurrence?.monthWeek || "first",
    },
    mode: "onSubmit",
  });

  // explicit watchers for recurrence dialog fields (so useEffect deps are stable)
  const rfEndType = recurrenceForm.watch("endType");
  const rfEndDate = recurrenceForm.watch("endDate");
  const rfInterval = recurrenceForm.watch("interval");
  const rfUnit = recurrenceForm.watch("unit");
  const rfDaysOfWeek = recurrenceForm.watch("daysOfWeek");
  const rfMonthOption = recurrenceForm.watch("monthOption");
  const rfCount = recurrenceForm.watch("count");

  const handleSaveRecurrence = (data) => {
    // Save recurrence data to the main form
    form.setValue("recurrence", data);
    setShowRecurrence(false);
  };

  // Watch values for auto-population and side effects
  const watchedClassMode = form.watch("classMode");
  const watchedDeliveredVia = form.watch("deliveredVia");
  const watchedCohortId = form.watch("cohortId");
  const watchedTmId = form.watch("tmId");
  const watchedOwnerId = form.watch("ownerId");

  const watchedDate = form.watch("date");
  const watchedStartTime = form.watch("startTime");
  const watchedAttendees = form.watch("attendees");

  const startDateObj = watchedDate
    ? parse(watchedDate, "yyyy-MM-dd", new Date())
    : null;
  const minEndDate =
    startDateObj && isValid(startDateObj)
      ? addDays(startDateObj, 1)
      : undefined;

  const selectedTmDetails = selectedTm
    ? allTms.find((tm) => String(tm.id) === String(selectedTm))
    : null;
  const selectedTmLabel = selectedTmDetails
    ? `${selectedTmDetails.first_name} ${selectedTmDetails.last_name}`
    : "No technical mentor selected";

  const populateOptions = [{ value: "cohort", label: "Cohort" }];

  // Keep dropdown label stable; don’t show selected cohorts (can get very long).
  const autoPopulateLabel = "Auto-populate by cohort/group";

  useEffect(() => {
    const resolvedOwnerId = String(watchedOwnerId || user?.id || "").trim();
    const rawTmId = String(watchedTmId || "").trim();
    const normalizedTmId =
      rawTmId && rawTmId !== resolvedOwnerId ? rawTmId : "";
    if (normalizedTmId !== selectedTm) {
      setSelectedTm(normalizedTmId);
    }
  }, [watchedTmId, watchedOwnerId, selectedTm, user?.id]);

  // Always keep owner represented as an attendee chip.
  useEffect(() => {
    const ownerId = String(watchedOwnerId || user?.id || "").trim();
    if (!ownerId) return;

    const currentAttendeesRaw = form.getValues("attendees");
    const currentAttendees = Array.isArray(currentAttendeesRaw)
      ? currentAttendeesRaw.map((id) => String(id)).filter(Boolean)
      : [];

    const next = new Set(currentAttendees);
    next.add(ownerId);

    form.setValue("attendees", Array.from(next), { shouldValidate: true });
  }, [watchedOwnerId, user?.id]);

  // Keep TM represented as an attendee chip (and avoid stale TM chips when TM changes).
  useEffect(() => {
    const currentTmId = (watchedTmId || "").trim();
    const prevTmId = (prevTmIdRef.current || "").trim();
    prevTmIdRef.current = currentTmId;

    const currentAttendeesRaw = form.getValues("attendees");
    const currentAttendees = Array.isArray(currentAttendeesRaw)
      ? currentAttendeesRaw.map((id) => String(id)).filter(Boolean)
      : [];

    const next = new Set(currentAttendees);

    if (prevTmId && prevTmId !== currentTmId) {
      next.delete(prevTmId);
    }
    if (currentTmId) {
      next.add(currentTmId);
    }

    form.setValue("attendees", Array.from(next), { shouldValidate: true });
  }, [watchedTmId]);

  // Fetch dropdown data on mount
  useEffect(() => {
    const fetchData = async () => {
      const token = await getToken();
      if (!token) return;
      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      try {
        const [cohortsRes, coursesRes, allTms, allUsers] = await Promise.all([
          axios.get(api_endpoint + "/cohort/all", reqConfig),
          axios.get(api_endpoint + "/course/", reqConfig),
          fetchAllPaginatedItems({
            url: api_endpoint + `/user/users/role/Instructor`,
            token,
            selectItems: (data) => data?.users ?? [],
            mode: "skip",
            pageSize: 200,
          }),
          fetchAllPaginatedItems({
            url: api_endpoint + `/user/users`,
            token,
            selectItems: (data) => data?.users ?? [],
            mode: "skip",
            pageSize: 200,
          }),
        ]);

        setCohorts(cohortsRes.data?.cohorts || []);
        setAllCourses(coursesRes.data?.courses || []);
        setAllTms(allTms || []);
        setAllUsers(allUsers || []);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchData();
  }, [getToken]);

  // Fetch students in cohort when cohortId changes
  useEffect(() => {
    if (watchedCohortId > 0) {
      const fetchStudents = async () => {
        const token = await getToken();
        if (!token) return;
        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        try {
          const enrolments = await fetchAllPaginatedItems({
            url: api_endpoint + `/cohort/enrolments/${watchedCohortId}`,
            token,
            headers: reqConfig.headers,
            mode: "skip",
            pageSize: 200,
            maxPages: 200,
            dedupe: false,
            selectItems: (data) => data?.enrolments ?? [],
          });
          setStudentsInCohort(
            (Array.isArray(enrolments) ? enrolments : []).map((item) => ({
              id: item.user_id || item.id,
              name:
                item.user_first_name && item.user_last_name
                  ? `${item.user_first_name} ${item.user_last_name}`
                  : "Not available",
              email: item.user_email || "Not available",
            })),
          );
        } catch {
          setStudentsInCohort([]);
        }
      };
      fetchStudents();
    }
  }, [watchedCohortId, getToken]);

  // Auto-fill location and room for online mode
  useEffect(() => {
    if (watchedClassMode === "online") {
      if (form.getValues("location") !== "Online") {
        form.setValue("location", "Online");
      }
      if (form.getValues("room") !== "none") {
        form.setValue("room", "none");
      }
    } else if (watchedClassMode === "physical") {
      if (form.getValues("location") !== "") {
        form.setValue("location", "");
      }
      if (form.getValues("room") !== "") {
        form.setValue("room", "");
      }
    }
  }, [watchedClassMode]);

  // Recurrence helpers
  function getOrdinal(dateStr) {
    const d = new Date(dateStr).getDate();
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  /// Generate RRule for recurrence preview (fixed for yearly and improved weekday mapping)
  function generateRRule(override = {}) {
    const recurrence = { ...watchedRecurrence, ...override };
    if (!watchedDate) return { rule: null, occurrences: [] };

    const [hours, minutes] = watchedStartTime
      ? watchedStartTime.split(":").map(Number)
      : [10, 0];
    const dtstart = new Date(watchedDate);
    dtstart.setHours(hours, minutes, 0, 0);

    if (recurrence.type === "none") return { rule: null, occurrences: [] };

    let freq;
    if (recurrence.type === "custom") {
      const unitMap = {
        Day: RRule.DAILY,
        Week: RRule.WEEKLY,
        Month: RRule.MONTHLY,
        Year: RRule.YEARLY,
      };
      const unit = recurrence.unit || "Week";
      freq = unitMap[unit];
      if (!freq) return { rule: null, occurrences: [] };
    } else if (recurrence.type === "daily") {
      freq = RRule.DAILY;
    } else if (recurrence.type === "weekly") {
      freq = RRule.WEEKLY;
    } else if (recurrence.type === "monthly") {
      freq = RRule.MONTHLY;
    } else if (recurrence.type === "yearly") {
      freq = RRule.YEARLY;
    } else {
      return { rule: null, occurrences: [] };
    }

    // base options
    let options = {
      freq,
      interval: recurrence.interval || 1,
      dtstart,
    };

    // End type: count or date
    if (recurrence.endType === "count") {
      options.count = recurrence.count || 1;
    } else if (recurrence.endType === "date" && recurrence.endDate) {
      const endDate = new Date(recurrence.endDate);
      // include the entire end day
      endDate.setHours(23, 59, 59, 999);
      options.until = endDate;
    }

    // Robust weekday map for byweekday uses
    const weekdayMap = {
      Sun: RRule.SU,
      Mon: RRule.MO,
      Tue: RRule.TU,
      Wed: RRule.WE,
      Thu: RRule.TH,
      Fri: RRule.FR,
      Sat: RRule.SA,
    };

    // Handle weekly recurrence - set byweekday for weekly or custom-weekly
    if (
      (recurrence.type === "weekly" ||
        (recurrence.type === "custom" && recurrence.unit === "Week")) &&
      recurrence.daysOfWeek?.length
    ) {
      options.byweekday = recurrence.daysOfWeek
        .map((d) => weekdayMap[d])
        .filter(Boolean);
    }

    // Handle monthly recurrence - set bymonthday or byweekday for custom-monthly
    if (recurrence.type === "custom" && recurrence.unit === "Month") {
      if (recurrence.monthOption === "dayOfMonth") {
        options.bymonthday = [dtstart.getDate()];
      } else if (recurrence.monthOption === "weekdayOfMonth") {
        // compute which weekday (1st, 2nd, etc.) this date is in the month
        const day = dtstart.getDate();
        const ordinal = Math.ceil(day / 7); // 1..5
        const weekdayStr = format(dtstart, "EEE");
        const rruleWeekday = weekdayMap[weekdayStr];
        if (rruleWeekday) options.byweekday = [rruleWeekday.nth(ordinal)];
      }
    }

    // Ensure yearly repeats on same month/day (helps edge cases)
    if (
      (recurrence.type === "custom" && recurrence.unit === "Year") ||
      recurrence.type === "yearly"
    ) {
      // RRule should handle it from dtstart
      options.bymonth = [dtstart.getMonth() + 1]; // month is 1-12 in RRule
      options.bymonthday = [dtstart.getDate()];
    }

    try {
      const rule = new RRule(options);
      return { rule, occurrences: rule.all() };
    } catch (error) {
      console.error("Error generating RRule:", error);
      return { rule: null, occurrences: [] };
    }
  }

  const { occurrences } = generateRRule();

  // Sync recurrence count when endType is date
  useEffect(() => {
    const { occurrences } = generateRRule({
      ...recurrenceForm.getValues(),
      date: watchedDate,
      startTime: watchedStartTime,
      type: "custom",
    });
    setDialogOccurrences(occurrences);

    if (rfEndType === "date" && rfEndDate && watchedDate && watchedStartTime) {
      if (occurrences && occurrences.length > 0) {
        const current = recurrenceForm.getValues("count");
        if (current !== occurrences.length) {
          recurrenceForm.setValue("count", occurrences.length);
        }
      } else {
        if (recurrenceForm.getValues("count") !== 1) {
          recurrenceForm.setValue("count", 1);
        }
      }
    }
  }, [
    rfEndType,
    rfEndDate,
    rfInterval,
    rfUnit,
    rfDaysOfWeek,
    rfMonthOption,
    rfCount,
    watchedDate,
    watchedStartTime,
  ]);

  // Handle auto-populate
  const handleAutoPopulate = async (strategy = "cohort") => {
    if (strategy !== "cohort") {
      toast.error("Selected populate method is not available yet");
      return;
    }

    const cohortId = selectedCohorts?.[0] ? String(selectedCohorts[0]) : "";
    if (!cohortId) {
      return;
    }

    const token = await getToken();
    const reqConfig = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const attendeeSet = new Set(
        (Array.isArray(form.getValues("attendees"))
          ? form.getValues("attendees")
          : []
        )
          .map((id) => String(id))
          .filter(Boolean),
      );

      const fetchedEnrolments = await fetchAllPaginatedItems({
        url: `${api_endpoint}/cohort/enrolments/${cohortId}`,
        token,
        headers: reqConfig.headers,
        mode: "skip",
        pageSize: 200,
        maxPages: 200,
        dedupe: false,
        selectItems: (data) => data?.enrolments ?? [],
      });
      fetchedEnrolments.forEach((item) => {
        const id = item.user_id || item.id;
        if (id) attendeeSet.add(String(id));
      });

      // Add TM if selected and not already in the list
      if (selectedTm) {
        attendeeSet.add(String(selectedTm));
      }

      const ownerId = String(
        form.getValues("ownerId") || user?.id || "",
      ).trim();
      if (ownerId) attendeeSet.add(ownerId);

      const formattedStudents = Array.from(attendeeSet);
      if (!formattedStudents.length) {
        toast.error("No attendees found for the selected cohorts");
        return;
      }

      // Set attendees to all user IDs in cohort
      form.setValue("attendees", formattedStudents);
      form.setValue("autoPopulate", 1);
      form.setValue("cohortId", Number(cohortId) || 0);
      form.setValue("courseId", Number(selectedCourse));
      form.setValue("tmId", selectedTm || "");

      await form.trigger("attendees");

      setShowStudentSelector(false);
      toast.success("Attendees auto-populated successfully");
    } catch (error) {
      toast.error("Failed to fetch cohort enrolments");
      console.error(error);
    }
  };

  const onSubmit = async (values) => {
    // Note: `values` contains the form state (including default recurrence fields).
    // The API request uses `payload` below, which intentionally omits `recurrence`
    // when user selects "Does not repeat".
    console.log("Submitting raw form values:", values);
    try {
      setIsSubmitting(true);
      const token = await getToken();
      if (!token) throw new Error("No auth token found");

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const recurrenceType = values?.recurrence?.type;
      const normalizedRecurrenceType = String(
        recurrenceType || "",
      ).toLowerCase();
      const shouldSendRecurrence =
        Boolean(recurrenceType) &&
        normalizedRecurrenceType !== "none" &&
        normalizedRecurrenceType !== "doesnotrepeat";

      // Recurrence is optional in the backend: when user selects "Does not repeat",
      // omit the field completely.
      const recurrencePayload = shouldSendRecurrence
        ? {
            type: values.recurrence.type,
            interval: values.recurrence.interval ?? 1,
            count: values.recurrence.count ?? 1,
            endType: values.recurrence.endType ?? "count",
            endDate: values.recurrence.endDate ?? "",
            daysOfWeek: values.recurrence.daysOfWeek ?? [],
            unit: values.recurrence.unit ?? "",
            monthOption: values.recurrence.monthOption ?? "",
            monthWeek: values.recurrence.monthWeek ?? "",
            monthWeekday: values.recurrence.monthWeekday ?? "",
          }
        : undefined;

      // Always add owner to attendees if not present
      const attendeeSet = new Set(
        (Array.isArray(values.attendees) ? values.attendees : [])
          .map((id) => String(id))
          .filter(Boolean),
      );

      const resolvedOwnerId = String(values.ownerId || user?.id || "").trim();
      const rawTmId = String(values.tmId || "").trim();
      const resolvedTmId =
        rawTmId && rawTmId !== resolvedOwnerId ? rawTmId : "";

      if (resolvedOwnerId) attendeeSet.add(resolvedOwnerId);
      // If user explicitly selected a TM, keep them included as well.
      if (resolvedTmId) attendeeSet.add(resolvedTmId);

      const attendees = Array.from(attendeeSet);

      // IMPORTANT: don't spread `values` directly, otherwise it always includes `values.recurrence`
      // and the backend may generate a default RRULE even when UI says "Does not repeat".
      const { recurrence: _ignoredRecurrence, ...restValues } = values || {};

      // Convert local times to UTC based on user's timezone preference
      const userTimezone = getUserTimezone();
      const utcStartTime = toUtcISOStringFromLocalDateAndTime(
        values.date,
        values.startTime,
        userTimezone,
      );
      const utcEndTime = toUtcISOStringFromLocalDateAndTime(
        values.date,
        values.endTime,
        userTimezone,
      );

      // Extract just the time portion (HH:mm:ss) from the UTC ISO string for the payload
      const utcStartTimeOnly = utcStartTime
        ? new Date(utcStartTime).toISOString().slice(11, 19)
        : values.startTime;
      const utcEndTimeOnly = utcEndTime
        ? new Date(utcEndTime).toISOString().slice(11, 19)
        : values.endTime;

      const payload = {
        ...restValues,
        ownerId: resolvedOwnerId,
        attendees,
        // Override startTime and endTime with UTC-converted times
        startTime: utcStartTimeOnly,
        endTime: utcEndTimeOnly,
        // Also include full ISO timestamps and timezone for reference
        startTimeUtc: utcStartTime,
        endTimeUtc: utcEndTime,
        timezone: userTimezone,
        ...(shouldSendRecurrence ? { recurrence: recurrencePayload } : {}),
      };

      // TM is optional: only send when explicitly selected.
      if (resolvedTmId) {
        payload.tmId = resolvedTmId;
      } else if (Object.prototype.hasOwnProperty.call(payload, "tmId")) {
        delete payload.tmId;
      }

      // Cohort is optional: avoid sending cohortId=0.
      if (Number(payload?.cohortId || 0) <= 0) {
        delete payload.cohortId;
      }

      // Absolute guard: ensure we never send `recurrence` when "Does not repeat".
      // (Even if a future refactor accidentally adds it back.)
      if (
        !shouldSendRecurrence &&
        Object.prototype.hasOwnProperty.call(payload, "recurrence")
      ) {
        delete payload.recurrence;
      }

      console.log("Create class payload (sent to API):", payload);
      console.log("Create class payload.recurrence:", payload?.recurrence);

      const response = await axios.post(
        api_endpoint + "/liveclass/create",
        payload,
        reqConfig,
      );
      console.log("Class created successfully:", response.data);
      toast.success(`Class "${values.title}" created successfully!`);
      setOpen(false);
      form.reset();
      if (fetchClasses) fetchClasses();
    } catch (error) {
      toast.error("Failed to create class");
      console.error(
        "Error creating class:",
        error?.response?.data || error.message,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="" className="w-full md:w-auto">
            Create Class +
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[90vh] overflow-y-auto border border-(--neutral-300) text-(--neutral-900) font-body">
          <DialogHeader className="border-(--neutral-300)">
            <DialogTitle className="font-heading text-(--neutral-900)">
              Add a new class
            </DialogTitle>
            <p className="text-(--neutral-800) font-body">
              Create a calendar event for your class, event, or meeting.
            </p>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              id="add-class-form"
            >
              <h3 className="font-semibold font-heading">General Details</h3>

              <FormField
                control={form.control}
                name="title"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Class Title*</FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="Intro to Machine Learning"
                        className={cn(
                          inputClasses,
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-destructive)",
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: fieldState.error.message }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Date*</FieldLabel>
                      <FieldContent>
                        <Popover
                          open={openDatePopover}
                          onOpenChange={setOpenDatePopover}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              className={cn(
                                "w-full justify-between font-normal rounded-md border border-(--neutral-300) text-(--neutral-900) hover:border-(--neutral-300) hover:bg-(--surface-base) hover:text-(--neutral-900) focus:ring-1 focus:ring-(--neutral-300)",
                                outlineButtonClasses,
                                fieldState.error &&
                                  "border-(--color-destructive) focus:ring-(--color-destructive)",
                              )}
                              type="button"
                            >
                              {field.value
                                ? format(new Date(field.value), "yyyy-MM-dd")
                                : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto overflow-hidden p-0 border border-(--neutral-300) bg-(--surface-base)"
                            align="start"
                            side="bottom"
                            sideOffset={6}
                            avoidCollisions={false}
                          >
                            <Calendar
                              mode="single"
                              disablePastDates={false}
                              selected={
                                field.value ? new Date(field.value) : undefined
                              }
                              captionLayout="dropdown"
                              onSelect={(date) => {
                                field.onChange(
                                  date ? format(date, "yyyy-MM-dd") : "",
                                );
                                setOpenDatePopover(false);
                              }}
                              fromYear={2000}
                              toYear={2100}
                              toDate={new Date(2100, 11, 31)}
                            />
                          </PopoverContent>
                        </Popover>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: fieldState.error.message }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="startTime"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Start Time*</FieldLabel>
                      <FieldContent>
                        <TimePicker
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          className={cn(
                            inputClasses,
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-destructive)",
                          )}
                        />
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: fieldState.error.message }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endTime"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>End Time*</FieldLabel>
                      <FieldContent>
                        <TimePicker
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          className={cn(
                            inputClasses,
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-destructive)",
                          )}
                        />
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: fieldState.error.message }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>

              {/* Timezone Warning */}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
                <span>
                  Times are relative to your timezone:{" "}
                  <strong>{getUserTimezone()}</strong>
                </span>
              </div>

              {/* Recurrence */}
              <FormField
                control={form.control}
                name="recurrence.type"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Recurrence</FieldLabel>
                    <FieldContent>
                      <Select
                        value={field.value}
                        open={recurrenceSelectOpen}
                        onOpenChange={setRecurrenceSelectOpen}
                        onValueChange={(value) => {
                          field.onChange(value);
                          setRecurrenceSelectOpen(false);
                          if (value === "custom") {
                            recurrenceForm.reset(form.getValues("recurrence")); // <-- Reset dialog form
                            setTimeout(() => setShowRecurrence(true), 0);
                            form.setValue("recurrence.unit", "Week");
                          } else {
                            setShowRecurrence(false);
                            setDialogOccurrences([]);
                          }
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            selectTriggerClasses,
                            fieldState.error &&
                              "border-(--color-destructive) focus:ring-(--color-destructive)",
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Does not repeat</SelectItem>
                          <SelectItem
                            value="custom"
                            onPointerDown={(e) => {
                              // Radix Select won't fire onValueChange if selecting the same value again.
                              // This makes "Custom" always open the dialog.
                              if (field.value === "custom") {
                                e.preventDefault();
                                setRecurrenceSelectOpen(false);
                                recurrenceForm.reset(
                                  form.getValues("recurrence"),
                                );
                                setTimeout(() => setShowRecurrence(true), 0);
                              }
                            }}
                          >
                            Custom
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: fieldState.error.message }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              {/* Preview occurrences */}
              {watchedRecurrence?.type !== "none" &&
                dialogOccurrences.length > 0 && (
                  <div>
                    <h3 className="font-semibold font-heading mb-2">
                      Occurrences
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {dialogOccurrences.map((d, i) => (
                        <span key={i} className="tag-chip">
                          {format(d, "yyyy-MM-dd HH:mm")}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              <FormField
                control={form.control}
                name="description"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Description
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Textarea
                        {...field}
                        placeholder="Brief description of what the class covers"
                        rows={3}
                        className={cn(
                          inputClasses,
                          "min-h-30",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-destructive)",
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: fieldState.error.message }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              <h3 className="font-semibold font-heading">Class Delivery</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="classType"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Class Type*</FieldLabel>
                      <FieldContent>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            className={cn(
                              selectTriggerClasses,
                              fieldState.error &&
                                "border-(--color-destructive) focus:ring-(--color-destructive)",
                            )}
                          >
                            <SelectValue placeholder="Class" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="class">Class</SelectItem>
                            <SelectItem value="event">Event</SelectItem>
                            <SelectItem value="meeting">Meeting</SelectItem>
                          </SelectContent>
                        </Select>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: fieldState.error.message }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="classMode"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Class Mode*</FieldLabel>
                      <FieldContent>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            className={cn(
                              selectTriggerClasses,
                              fieldState.error &&
                                "border-(--color-destructive) focus:ring-(--color-destructive)",
                            )}
                          >
                            <SelectValue placeholder="Online" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="physical">In-Person</SelectItem>
                          </SelectContent>
                        </Select>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: fieldState.error.message }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deliveredVia"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Delivered Via*</FieldLabel>
                      <FieldContent>
                        <Select
                          value={field.value}
                          onValueChange={(value) => {
                            // These options are intentionally disabled (UI shows tooltip),
                            // but Radix Select can still attempt selection via keyboard/mouse.
                            // Guard here to ensure the form value never updates to them.
                            if (value === "mirotalk" || value === "teams") return;
                            field.onChange(value);
                          }}
                        >
                          <SelectTrigger
                            className={cn(
                              selectTriggerClasses,
                              "truncate",
                              fieldState.error &&
                                "border-(--color-destructive) focus:ring-(--color-destructive)",
                            )}
                          >
                            <SelectValue placeholder="Select delivery" />
                          </SelectTrigger>
                          <SelectContent>
                            <Popover open={deliveredViaHover === "mirotalk"}>
                              <PopoverAnchor asChild>
                                <SelectItem
                                  value="mirotalk"
                                  className="opacity-50 cursor-not-allowed"
                                  onMouseEnter={() =>
                                    setDeliveredViaHover("mirotalk")
                                  }
                                  onMouseLeave={() =>
                                    setDeliveredViaHover((v) =>
                                      v === "mirotalk" ? null : v,
                                    )
                                  }
                                  onPointerDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }
                                  }}
                                >
                                  Mirotalk WebRTC
                                </SelectItem>
                              </PopoverAnchor>
                              <PopoverContent
                                side="right"
                                align="start"
                                sideOffset={8}
                                className="w-56 p-2 text-xs"
                                style={{ zIndex: 10001 }}
                                onMouseEnter={() =>
                                  setDeliveredViaHover("mirotalk")
                                }
                                onMouseLeave={() => setDeliveredViaHover(null)}
                              >
                                Still under development, will be enabled when
                                ready
                              </PopoverContent>
                            </Popover>

                            <Popover open={deliveredViaHover === "teams"}>
                              <PopoverAnchor asChild>
                                <SelectItem
                                  value="teams"
                                  className="opacity-50 cursor-not-allowed"
                                  onMouseEnter={() =>
                                    setDeliveredViaHover("teams")
                                  }
                                  onMouseLeave={() =>
                                    setDeliveredViaHover((v) =>
                                      v === "teams" ? null : v,
                                    )
                                  }
                                  onPointerDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }
                                  }}
                                >
                                  Google Meet
                                </SelectItem>
                              </PopoverAnchor>
                              <PopoverContent
                                side="right"
                                align="start"
                                sideOffset={8}
                                className="w-56 p-2 text-xs"
                                style={{ zIndex: 10001 }}
                                onMouseEnter={() =>
                                  setDeliveredViaHover("teams")
                                }
                                onMouseLeave={() => setDeliveredViaHover(null)}
                              >
                                Still under development, will be enabled when
                                ready
                              </PopoverContent>
                            </Popover>
                            <SelectItem value="zoom">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: fieldState.error.message }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>

              {watchedDeliveredVia === "zoom" && (
                  <FormField
                    control={form.control}
                    name="meetingLink"
                    render={({ field, fieldState }) => (
                      <Field>
                        <FieldLabel>Meeting Link*</FieldLabel>
                        <FieldContent>
                          <Input
                            {...field}
                            placeholder="https://zoom.us/j/1234567890"
                            className={cn(
                              inputClasses,
                              fieldState.error &&
                                "border-(--color-destructive) focus-visible:ring-(--color-destructive)",
                            )}
                          />
                          <FieldError
                            errors={
                              fieldState.error
                                ? [{ message: fieldState.error.message }]
                                : []
                            }
                          />
                        </FieldContent>
                      </Field>
                    )}
                  />
                )}

              {watchedClassMode !== "online" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field, fieldState }) => (
                      <Field>
                        <FieldLabel>Location*</FieldLabel>
                        <FieldContent>
                          <Input
                            {...field}
                            placeholder="Circular Innovation Hub, 233 Owashika Road"
                            className={cn(
                              inputClasses,
                              fieldState.error &&
                                "border-(--color-destructive) focus-visible:ring-(--color-destructive)",
                            )}
                          />
                          <FieldError
                            errors={
                              fieldState.error
                                ? [{ message: fieldState.error.message }]
                                : []
                            }
                          />
                        </FieldContent>
                      </Field>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="room"
                    render={({ field, fieldState }) => (
                      <Field>
                        <FieldLabel>Room</FieldLabel>
                        <FieldContent>
                          <Input
                            {...field}
                            placeholder="CIH Boardroom"
                            className={cn(
                              inputClasses,
                              fieldState.error &&
                                "border-(--color-destructive) focus-visible:ring-(--color-destructive)",
                            )}
                          />
                          <FieldError
                            errors={
                              fieldState.error
                                ? [{ message: fieldState.error.message }]
                                : []
                            }
                          />
                        </FieldContent>
                      </Field>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="attendees"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Attendees*</FieldLabel>
                    <FieldContent>
                      <div className="flex flex-col gap-2">
                        <div className="flex w-full items-stretch gap-0.5 ">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowAttendeesDialog(true)}
                            className={cn(
                              "flex-1 w-full min-w-0 rounded-l-md rounded-r-none",
                              fieldState.error &&
                                "border-(--color-destructive)",
                            )}
                          >
                            {field.value.length > 0
                              ? `${field.value.length} attendee(s) selected`
                              : "Select attendees"}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="flex-none w-12 rounded-l-none rounded-r-md px-0 flex items-center justify-center"
                                aria-label="More attendee actions"
                              >
                                <ChevronDownIcon className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="min-w-55"
                            >
                              <DropdownMenuItem
                                onSelect={(event) => {
                                  event.preventDefault();
                                  setShowStudentSelector(true);
                                }}
                              >
                                {autoPopulateLabel}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={(event) => {
                                  event.preventDefault();
                                  setShowBulkAttendeesDialog(true);
                                }}
                              >
                                Bulk upload attendees
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        {(() => {
                          const resolvedOwnerId = String(
                            watchedOwnerId || user?.id || "",
                          ).trim();
                          const rawTmId = String(watchedTmId || "").trim();
                          const hasExplicitTm = Boolean(
                            rawTmId && rawTmId !== resolvedOwnerId,
                          );
                          return hasExplicitTm;
                        })() ? (
                          <p className="text-xs text-(--neutral-600) font-body">
                            TM: {selectedTmLabel} (included)
                          </p>
                        ) : (
                          <p className="text-xs text-(--neutral-600) font-body">
                            Owner (included)
                          </p>
                        )}
                      </div>
                    </FieldContent>
                    {field.value.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {field.value.map((userId) => {
                          const userObj =
                            allUsers.find(
                              (u) => String(u.id) === String(userId),
                            ) ||
                            allTms.find(
                              (tm) => String(tm.id) === String(userId),
                            );
                          const resolvedOwnerId = String(
                            watchedOwnerId || user?.id || "",
                          ).trim();
                          const explicitTmId = String(watchedTmId || "").trim();
                          const hasExplicitTm = Boolean(
                            explicitTmId && explicitTmId !== resolvedOwnerId,
                          );
                          const isOwnerChip =
                            resolvedOwnerId &&
                            String(userId) === String(resolvedOwnerId);
                          const isTmChip =
                            hasExplicitTm &&
                            String(userId) === String(explicitTmId);
                          const showOwnerBadge = !hasExplicitTm && isOwnerChip;
                          const canRemoveChip = !isOwnerChip;
                          return (
                            <div key={userId} className="tag-chip">
                              <span className="font-heading">
                                {userObj
                                  ? `${userObj.first_name} ${userObj.last_name}`
                                  : userId}
                              </span>
                              {isTmChip ? (
                                <span className="ml-2 rounded-full px-2 py-0.5 text-[10px] font-body text-(--color-primary)">
                                  TM
                                </span>
                              ) : showOwnerBadge ? (
                                <span className="ml-2 rounded-full px-2 py-0.5 text-[10px] font-body text-(--color-primary)">
                                  Owner
                                </span>
                              ) : null}
                              {isTmChip ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Clear selected TM and remove it from attendees.
                                    form.setValue("tmId", "", {
                                      shouldValidate: true,
                                    });
                                    field.onChange(
                                      field.value.filter(
                                        (id) => String(id) !== String(userId),
                                      ),
                                    );
                                  }}
                                  className="chip-remove"
                                  aria-label="Remove selected technical mentor"
                                >
                                  ×
                                </button>
                              ) : canRemoveChip ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    field.onChange(
                                      field.value.filter(
                                        (id) => String(id) !== String(userId),
                                      ),
                                    );
                                  }}
                                  className="chip-remove"
                                >
                                  ×
                                </button>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <FieldError
                      errors={
                        fieldState.error
                          ? [{ message: fieldState.error.message }]
                          : []
                      }
                    />
                  </Field>
                )}
              />
            </form>
          </Form>
          <DialogFooter className="border-(--neutral-300) pt-3">
            <DialogClose asChild>
              <Button
                variant="outline"
                type="button"
                onClick={handleCancel}
                className={cn("", outlineButtonClasses)}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="default"
              form="add-class-form"
              className={cn(" ", isSubmitting && "opacity-70")}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center w-full">
                  <ClipLoader size={20} color="#fff" />
                </span>
              ) : (
                "Create Class"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurrence dialog */}
      {showRecurrence && (
        <Dialog open={showRecurrence} onOpenChange={setShowRecurrence}>
          <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-105 h-auto border border-(--neutral-300) htext-(--neutral-900) font-body">
            <DialogHeader className="border-(--neutral-300)">
              <DialogTitle className="font-heading text-(--neutral-900)">
                Custom recurrence
              </DialogTitle>
              <DialogDescription className="text-(--neutral-800) font-body">
                Set up a custom recurrence pattern for this class.
              </DialogDescription>
            </DialogHeader>
            <Form {...recurrenceForm}>
              <form
                id="recurrence-form"
                onSubmit={recurrenceForm.handleSubmit((data) => {
                  // Always recalculates occurrences before saving
                  const { occurrences } = generateRRule({
                    ...data,
                    date: watchedDate,
                    startTime: watchedStartTime,
                    type: "custom",
                  });
                  const count =
                    data.endType === "date" ? occurrences.length : data.count;
                  form.setValue("recurrence", {
                    ...data,
                    type: "custom",
                    count,
                  });
                  setShowRecurrence(false);
                })}
                className="space-y-4"
              >
                <div>
                  <Label className="mb-2 font-heading font-semibold">
                    Repeats Every *
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Controller
                      name="interval"
                      control={recurrenceForm.control}
                      rules={{
                        required: "Interval is required",
                        min: {
                          value: 1,
                          message: "Interval must be at least 1",
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min={1}
                          value={field.value}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                          className={cn(
                            "w-20",
                            inputClasses,
                            recurrenceForm.formState.errors?.interval &&
                              "border-(--color-destructive) focus-visible:ring-(--color-destructive)",
                          )}
                        />
                      )}
                    />
                    <Controller
                      name="unit"
                      control={recurrenceForm.control}
                      rules={{ required: "Unit is required" }}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            className={cn(
                              "flex-1",
                              selectTriggerClasses,
                              recurrenceForm.formState.errors?.unit &&
                                "border-(--color-destructive) focus:ring-(--color-destructive)",
                            )}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Day">Day</SelectItem>
                            <SelectItem value="Week">Week</SelectItem>
                            <SelectItem value="Month">Month</SelectItem>
                            <SelectItem value="Year">Year</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  {(recurrenceForm.formState.errors?.interval ||
                    recurrenceForm.formState.errors?.unit) && (
                    <p className="mt-1 text-xs text-(--color-destructive)">
                      {recurrenceForm.formState.errors?.interval?.message ||
                        recurrenceForm.formState.errors?.unit?.message}
                    </p>
                  )}
                </div>

                {/* Weekday selection */}
                {recurrenceForm.watch("unit") === "Week" && (
                  <div>
                    <Label className="mb-2 font-heading font-semibold">
                      Repeats On
                    </Label>
                    <div className="flex flex-col gap-2 mt-1">
                      {/* First row: Mon, Tue, Wed */}
                      <div className="grid grid-cols-4 gap-2">
                        {["Mon", "Tue", "Wed", "Thu"].map((day) => (
                          <Controller
                            key={day}
                            name="daysOfWeek"
                            control={recurrenceForm.control}
                            render={({ field }) => (
                              <Label
                                className={cn(
                                  "flex items-center gap-2 font-body font-semibold border rounded-md p-2 justify-center text-(--neutral-800)",
                                  field.value?.includes(day)
                                    ? "border-(--color-primary)"
                                    : "border-(--neutral-300)",
                                )}
                              >
                                <Checkbox
                                  checked={field.value?.includes(day)}
                                  onCheckedChange={() => {
                                    const exists = field.value?.includes(day);
                                    const newDays = exists
                                      ? field.value.filter((d) => d !== day)
                                      : [...(field.value || []), day];
                                    field.onChange(newDays);
                                  }}
                                />
                                {day}
                              </Label>
                            )}
                          />
                        ))}
                      </div>
                      {/* Second row: Thu, Fri, Sat */}
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {["Fri", "Sat", "Sun"].map((day) => (
                          <Controller
                            key={day}
                            name="daysOfWeek"
                            control={recurrenceForm.control}
                            render={({ field }) => (
                              <Label
                                className={cn(
                                  "flex items-center gap-2 font-body font-semibold border rounded-md p-2 justify-center text-(--neutral-800)",
                                  field.value?.includes(day)
                                    ? "border-(--color-primary)"
                                    : "border-(--neutral-300)",
                                )}
                              >
                                <Checkbox
                                  checked={field.value?.includes(day)}
                                  onCheckedChange={() => {
                                    const exists = field.value?.includes(day);
                                    const newDays = exists
                                      ? field.value.filter((d) => d !== day)
                                      : [...(field.value || []), day];
                                    field.onChange(newDays);
                                  }}
                                />
                                {day}
                              </Label>
                            )}
                          />
                        ))}

                        <Controller
                          name="daysOfWeek"
                          control={recurrenceForm.control}
                          render={({ field }) => (
                            <Label
                              className={cn(
                                "flex items-center gap-2 font-body font-semibold border rounded-md p-1 justify-center text-(--neutral-800)",
                                field.value?.length === 7
                                  ? "border-(--color-primary)"
                                  : "border-(--neutral-300)",
                              )}
                            >
                              <Checkbox
                                checked={field.value?.length === 7}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    field.onChange([
                                      "Mon",
                                      "Tue",
                                      "Wed",
                                      "Thu",
                                      "Fri",
                                      "Sat",
                                      "Sun",
                                    ]);
                                  } else {
                                    field.onChange([]);
                                  }
                                }}
                              />
                              All days
                            </Label>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Monthly options */}
                {recurrenceForm.watch("unit") === "Month" && (
                  <div>
                    <Label className="mb-2 font-heading font-semibold">
                      Monthly Repeat Options
                    </Label>
                    <Controller
                      name="monthOption"
                      control={recurrenceForm.control}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            className={cn("w-full mt-1", selectTriggerClasses)}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dayOfMonth">
                              {watchedDate
                                ? `Repeat on the ${new Date(watchedDate).getDate()}${getOrdinal(
                                    watchedDate,
                                  )} day of the month`
                                : "Select a date first"}
                            </SelectItem>
                            <SelectItem value="weekdayOfMonth">
                              {watchedDate
                                ? `Repeat on the first ${format(
                                    new Date(watchedDate),
                                    "EEEE",
                                  )} of the month`
                                : "Select a date first"}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                )}

                {/* End date and occurrences */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label className="mb-2 font-heading font-semibold">
                      Ends On *
                    </Label>
                    <Controller
                      name="endDate"
                      control={recurrenceForm.control}
                      rules={{ required: "End date is required" }}
                      render={({ field }) => (
                        <Popover
                          open={openRecurrenceDatePopover}
                          onOpenChange={setOpenRecurrenceDatePopover}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              className={cn(
                                "w-full justify-between font-normal rounded-md border border-(--neutral-300) text-(--neutral-900) hover:border-(--neutral-300) hover:bg-(--surface-base) hover:text-(--neutral-900) focus:ring-1 focus:ring-(--neutral-300)",
                                outlineButtonClasses,
                              )}
                              type="button"
                            >
                              {field.value
                                ? format(new Date(field.value), "yyyy-MM-dd")
                                : "Select date"}
                              <ChevronDownIcon />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto overflow-hidden p-0 border border-(--neutral-300) bg-(--surface-base)"
                            align="start"
                            side="top"
                            sideOffset={6}
                            avoidCollisions={false}
                          >
                            <Calendar
                              mode="single"
                              disablePastDates={!minEndDate}
                              selected={
                                field.value ? new Date(field.value) : undefined
                              }
                              captionLayout="dropdown"
                              onSelect={(date) => {
                                const dateStr = date
                                  ? format(date, "yyyy-MM-dd")
                                  : "";
                                field.onChange(dateStr);
                                recurrenceForm.setValue("endType", "date");
                                recurrenceForm.clearErrors("endDate");
                                // immediately regenerate occurrences and sync count so UI updates without reopening
                                const override = {
                                  ...recurrenceForm.getValues(),
                                  endDate: dateStr,
                                };
                                const { occurrences } = generateRRule({
                                  ...override,
                                  date: watchedDate,
                                  startTime: watchedStartTime,
                                  type: "custom",
                                });
                                setDialogOccurrences(occurrences);
                                if (occurrences && occurrences.length > 0) {
                                  recurrenceForm.setValue(
                                    "count",
                                    occurrences.length,
                                  );
                                } else {
                                  recurrenceForm.setValue("count", 1);
                                }
                                setOpenRecurrenceDatePopover(false);
                              }}
                              fromYear={2000}
                              toYear={2100}
                              fromDate={minEndDate ?? new Date()}
                              disabled={
                                minEndDate ? { before: minEndDate } : undefined
                              }
                              toDate={new Date(2100, 11, 31)}
                              defaultMonth={
                                field.value ? new Date(field.value) : undefined
                              }
                              dayHoverClassName="enabled:hover:[background-color:var(--neutral-100)]"
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    />
                    {recurrenceForm.formState.errors?.endDate && (
                      <p className="mt-1 text-xs text-(--color-destructive)">
                        {recurrenceForm.formState.errors.endDate.message}
                      </p>
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="mb-2 font-heading font-semibold">
                      Occurrences
                    </Label>
                    <Controller
                      name="count"
                      control={recurrenceForm.control}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min={1}
                          value={field.value}
                          onChange={(e) => {
                            // Only allow manual change if endType is "count"
                            if (recurrenceForm.watch("endType") === "count") {
                              field.onChange(Number(e.target.value));
                            }
                          }}
                          className={cn("", inputClasses)}
                          disabled={recurrenceForm.watch("endType") === "date"}
                        />
                      )}
                    />
                  </div>
                </div>
              </form>
            </Form>
            <DialogFooter className="">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRecurrence(false)}
                className={cn("", outlineButtonClasses)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="recurrence-form"
                variant="default"
                className="text-white"
              >
                Save Recurrence
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Separate Dialog Components */}
      <StudentSelectorDialog
        open={showStudentSelector}
        onOpenChange={setShowStudentSelector}
        cohorts={cohorts}
        allCourses={allCourses}
        allTms={allTms}
        selectedCohorts={selectedCohorts}
        setSelectedCohorts={setSelectedCohorts}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        selectedTm={selectedTm}
        onSelectTm={syncSelectedTm}
        populateBy={populateBy}
        onPopulateByChange={setPopulateBy}
        populateOptions={populateOptions}
        onAutoPopulate={handleAutoPopulate}
      />

      <AttendeesDialog
        open={showAttendeesDialog}
        onOpenChange={setShowAttendeesDialog}
        allUsers={allUsers}
        allTms={allTms}
        selectedAttendees={watchedAttendees || []}
        onAttendeesChange={(newAttendees) => {
          const next = new Set(
            (Array.isArray(newAttendees) ? newAttendees : [])
              .map((id) => String(id))
              .filter(Boolean),
          );
          const ownerId = String(watchedOwnerId || user?.id || "").trim();
          if (ownerId) next.add(ownerId);
          if (watchedTmId) next.add(String(watchedTmId));
          form.setValue("attendees", Array.from(next), {
            shouldValidate: true,
          });
        }}
      />

      <AttendeesBulkDialog
        open={showBulkAttendeesDialog}
        onOpenChange={setShowBulkAttendeesDialog}
        allUsers={allUsers}
        allTms={allTms}
        selectedAttendees={watchedAttendees || []}
        onAttendeesSelected={(newIds) => {
          const next = new Set(
            (Array.isArray(newIds) ? newIds : [])
              .map((id) => String(id))
              .filter(Boolean),
          );
          const ownerId = String(watchedOwnerId || user?.id || "").trim();
          if (ownerId) next.add(ownerId);
          if (watchedTmId) next.add(String(watchedTmId));
          form.setValue("attendees", Array.from(next), {
            shouldValidate: true,
          });
        }}
      />
    </>
  );
}
